import { bemClassBuilder } from '@vista-digital/bem-class-builder'
import { getAttributesByIds, getFilmAvailability, getFilmById, getScreenById } from '@vista-digital/occ-domain'
import {
    ShowtimeButtonProps,
    useI18n,
    useOccDisplayText,
    getHeroAttributesForShowtime,
    getPromotedAttributeForShowtime,
    getStandardAttributesForShowtime,
    Link,
    AttributeList,
    Button,
    ButtonColor,
} from '@vista-digital/occ-ui'
import { observer } from 'mobx-react'
import React, { useState, useMemo, useCallback } from 'react'
import { Attribute } from '@vista-digital/occ-ui/src/lib/subcomponents/attribute/attribute'
import { Modal, ShowtimeSeatPreviewComponent } from '@vista-digital/occ-ui'
import { Showtime } from '@vista-digital/ocapi-types/v1'
import { getSeatAvailabilityPreviewSummaryForShowtime } from '@vista-digital/occ-domain'

const b = bemClassBuilder('v-showtime-button')

export const CustomShowtimeButton: React.FC<ShowtimeButtonProps> = observer(props => {
    const i18n = useI18n()
    const { showtime: showtimeDisplayText } = useOccDisplayText()
    const { showtime, hiddenAttributeIds, ianaTimeZoneName, displayText, config, cdnOptions, onShowtimeClicked } = props
    const { showtimeUrlFactory, showScreenName } = config
    const { attributeIds, schedule, screenId, isSoldOut } = showtime

    const attributes = getAttributesByIds(attributeIds)
    const heroAttribute = getHeroAttributesForShowtime(attributes, i18n)[0]
    const promotedAttribute = getPromotedAttributeForShowtime(attributes, i18n)
    const standardAttributes = getStandardAttributesForShowtime(attributes, i18n, hiddenAttributeIds)
    const [showSeatPreview, setShowSeatPreview] = useState<boolean>(false)

    const timeString = i18n.date(schedule.startsAt, {
        format: displayText.showtimeTimeFormat,
        ianaTimeZoneName: ianaTimeZoneName,
    })

    const amPmString = i18n.date(schedule.startsAt, {
        format: displayText.showtimeAmPmFormat,
        ianaTimeZoneName: ianaTimeZoneName,
    })

    const screen = getScreenById(screenId)

    if (!screen) throw new Error(`No screen available for screen id: '${showtime.screenId}'.`)

    const showtimeUrl = useMemo(() => showtimeUrlFactory?.(showtime), [showtimeUrlFactory, showtime])

    const handleShowtimeClicked = useCallback(() => {
        onShowtimeClicked?.(showtime)
    }, [onShowtimeClicked, showtime])

    const handleModalClose = () => {
        setShowSeatPreview(false)
    }

    const showtimeSeatAvailabilitySummary = getSeatAvailabilityPreviewSummaryForShowtime(showtime.id)
    const availableSeats = showtimeSeatAvailabilitySummary?.availableSeats
    const totalSeats = showtimeSeatAvailabilitySummary?.totalSeats
    const showtimeSeatAvailabilityPercentage =
        totalSeats !== undefined && availableSeats !== undefined ? (availableSeats / totalSeats) * 100 : undefined

    const headerProps: SeatMapModalHeaderProps = {
        showtime,
        timeString,
        amPmString,
    }

    const footerProps: SeatMapModalFooterProps = {
        showtimeUrl: showtimeUrl,
        showtimeId: showtime.id,
        isSoldOut: isSoldOut,
        showtimeSeatAvailabilityPercentage: showtimeSeatAvailabilityPercentage,
        onPrimaryButtonClick: handleShowtimeClicked,
        onSecondaryButtonClick: handleModalClose,
    }

    return (
        <>
            <Link className={b.block()} href={showtimeUrl} onClick={handleShowtimeClicked} disabled={isSoldOut}>
                <div className={b.element('detail-wrapper')}>
                    <div className={b.element('detail-start-time-wrapper')}>
                        <time
                            className={b.element('detail-start-time')}
                            dateTime={new Date(schedule.startsAt).toISOString()}
                        >
                            {timeString}
                        </time>
                        {amPmString.length > 0 && (
                            <span className={b.element('detail-start-time-ampm')}>{amPmString}</span>
                        )}
                    </div>

                    {showScreenName && <div className={b.element('screen-name')}>{i18n.translatable(screen.name)}</div>}
                    {isSoldOut && <div className={b.element('sold-out')}>{showtimeDisplayText.soldOutLabel}</div>}

                    <AttributeList
                        className={b.element('attribute-list')}
                        attributes={standardAttributes}
                        showDetails={false}
                        iconCdnOptions={cdnOptions.attributeIcon}
                    />
                </div>

                {heroAttribute && (
                    <Attribute
                        attribute={heroAttribute}
                        showDetails={false}
                        cdnOptions={cdnOptions.heroAttributeIcon}
                    />
                )}

                {promotedAttribute && (
                    <Attribute
                        attribute={promotedAttribute}
                        showDetails={false}
                        cdnOptions={cdnOptions.promotedAttributeIcon}
                    />
                )}
            </Link>
            <button className="v-showtime-button__seat-map" onClick={() => setShowSeatPreview(true)}>
                Preview Seats
            </button>
            <Modal
                className="seat-map-modal"
                show={showSeatPreview}
                onClose={() => setShowSeatPreview(false)}
                header={<SeatMapModalHeader {...headerProps} />}
                footer={<SeatMapModalFooter {...footerProps} />}
            >
                <ShowtimeSeatPreviewComponent showtimeId={showtime.id} />
            </Modal>
        </>
    )
})

export default CustomShowtimeButton

interface SeatMapModalHeaderProps {
    showtime: Showtime
    timeString: string
    amPmString: string
}

const SeatMapModalHeader: React.FC<SeatMapModalHeaderProps> = props => {
    const { showtime, timeString, amPmString } = props
    const film = getFilmById(showtime.filmId)
    const date = new Date(showtime.schedule.startsAt.replace(/-/g, '/').replace(/T.+/, ''))

    const daysOfTheWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

    const monthsOfTheYear = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

    const formatDate = () => {
        var dayName: string = daysOfTheWeek[date.getDay()]
        var dayNumber = date.getDate()
        var month = monthsOfTheYear[date.getMonth()]

        return dayName + ' ' + dayNumber + ' ' + month
    }

    const formatTime = () => {
        return timeString.length > 0 && amPmString.length > 0 ? timeString + amPmString : ''
    }

    return <div className="v-modal-header__text">{film.title.text + ' - ' + formatDate() + ', ' + formatTime()}</div>
}

interface SeatMapModalFooterProps {
    showtimeUrl: string
    showtimeId: string
    isSoldOut: boolean
    showtimeSeatAvailabilityPercentage: number
    onSecondaryButtonClick: any
    onPrimaryButtonClick: any
}

const SeatMapModalFooter: React.FC<SeatMapModalFooterProps> = props => {
    const { showtimeUrl, isSoldOut, showtimeSeatAvailabilityPercentage, onPrimaryButtonClick, onSecondaryButtonClick } =
        props
    const i18n = useI18n()

    return (
        <>
            {showtimeSeatAvailabilityPercentage && (
                <p className="showtime-seat-availability">
                    Availability: {i18n.number(showtimeSeatAvailabilityPercentage, { decimalPrecision: 0 })}%
                </p>
            )}
            <div className="v-modal-footer__content--buttons">
                <Button color={ButtonColor.Secondary} onClick={onSecondaryButtonClick} label="Close" />
                <Link className="seat-map-modal__book-tickets" href={showtimeUrl} disabled={isSoldOut}>
                    <Button
                        color={ButtonColor.Primary}
                        onClick={onPrimaryButtonClick}
                        disabled={isSoldOut}
                        label="Buy tickets"
                    />
                </Link>
            </div>
        </>
    )
}
